import { useEffect, useRef, useState } from 'react';
import { fieldDir } from '../i18n/field-dir';
import { RichText } from '../i18n/RichText';
import { formatNumber } from '../i18n/format';
import { caughtErrorMessage, serverErrorMessage } from '../i18n/server-errors';
import { getCurrentLocale, t } from '../services/language-manager';
import {
  BUSINESS_CONTEXT_MAX_LENGTH,
  CATALOG_TRANSFER_FAILED,
  CatalogTransferError,
  downloadActionCatalog,
  uploadActionCatalog,
  WEBHOOK_SECRET_MAX_LENGTH,
  type CatalogImportSummary,
  type CatalogTransferProgress,
  type CatalogSummary,
  type TestWebhookResult,
} from '../services/wizard-backend';
import {
  buildSampleWebhookPayload,
  catalogProductsToCsv,
  downloadTemplateCsv,
  downloadTextFile,
  precheckCatalogFile,
} from '../services/actions-catalog';
import { sampleCustomFieldValues } from '../services/actions-custom-fields';
import { copyText } from '../services/clipboard';
import {
  actionTypeDescription,
  actionTypeLabel,
  isAiReply,
  isConversationInfo,
  isSendFiles,
  typedKey,
  usesCatalog,
} from '../services/action-types';
import { isReplyLanguage, replyLanguageLabel, replyLanguageOptionGroups } from '../services/reply-languages';
import type { GenesysDivision } from '../services/genesys-api';
import { ACTIONS_CATALOG_HELP_URL, ACTIONS_WEBHOOK_HELP_URL, SEND_FILES_HELP_URL } from '../config/wizard-config';
import type { EditableAction } from './ConversationActionsSection';
import { SaveButton, SaveRow, type SaveMessage } from './SaveFeedback';
import { CustomFieldsEditor, discardButtonProps, type CustomFieldsServerError } from './CustomFieldsEditor';
import { InfoFieldsEditor, type InfoFieldsServerError } from './InfoFieldsEditor';
import { CatalogRowManager } from './CatalogRowManager';
import { catalogLoaded, enableRequirements, readinessInstruction } from './ActionReadiness';
import { BilledIcon, BilledMark } from './BilledMark';

/**
 * Catalog CSV column reference shown in the "What each column means"
 * disclosure. Must stay true to the authoritative import rules in the toolbar
 * admin API (adminActionsConfig.ts handleCatalogImport): sku + product_name
 * required, everything else optional, invalid cells skip the row.
 */
const CATALOG_COLUMNS: Array<{ name: string; required: boolean; descKey: string }> = [
  { name: 'sku', required: true, descKey: 'settings-actions-columns-sku' },
  { name: 'product_name', required: true, descKey: 'settings-actions-columns-product-name' },
  { name: 'description', required: false, descKey: 'settings-actions-columns-description' },
  { name: 'unit', required: false, descKey: 'settings-actions-columns-unit' },
  { name: 'package_size', required: false, descKey: 'settings-actions-columns-package-size' },
  { name: 'aliases', required: false, descKey: 'settings-actions-columns-aliases' },
  { name: 'active', required: false, descKey: 'settings-actions-columns-active' },
];

// RFC 7230 token, max 100 chars: the save rule (wizard backend and toolbar admin API).
const HEADER_NAME_RE = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]{1,100}$/;
// Genesys division ids are UUIDs: the save rule (wizard backend and toolbar admin API DIVISION_ID_REGEX).
export const DIVISION_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The division IDs a save sends: the ticked ones, or the manual input when the divisions list is unavailable. */
export function effectiveDivisionIds(action: EditableAction, divisionsAvailable: boolean): string[] {
  if (divisionsAvailable) return action.divisionIds;
  return action.divisionsText
    .split(',')
    .map((d) => d.trim())
    .filter((d) => d.length > 0);
}

/**
 * What a webhook test still needs (an error string key), or null once it can
 * run. The secret is the HMAC signing key of every call whatever the auth
 * type, so a new action needs one typed (a saved one has it stored); custom
 * header auth needs the header name. The dialog's sample carries the label.
 */
export function webhookTestNeed(action: EditableAction): string | null {
  if (!action.actionId && !action.label.trim()) return 'settings-actions-error-label';
  const url = action.webhookUrl.trim();
  let urlValid = url.startsWith('https://');
  try {
    void new URL(url);
  } catch {
    urlValid = false;
  }
  if (!urlValid) return 'settings-actions-error-webhook';
  if (!action.secret && !action.hasSecret) return 'settings-actions-error-secret';
  if (action.secret.length > WEBHOOK_SECRET_MAX_LENGTH) return 'settings-actions-error-secret-too-long';
  if (action.authType === 'header' && !HEADER_NAME_RE.test(action.headerName.trim())) return 'settings-actions-error-header-name';
  if (!Number.isFinite(action.timeoutSeconds) || action.timeoutSeconds < 3 || action.timeoutSeconds > 20) {
    return 'settings-actions-error-timeout';
  }
  return null;
}

/**
 * Card inputs a save error can point at. Each but `custom-fields` and
 * `info-fields` is the id stem of its input (`action-<field>-<key>`); those two
 * focus the first input their editor marks invalid, and divisions with the
 * list shown the first ticked ID that is not a division ID.
 */
export type ActionField =
  | 'type'
  | 'label'
  | 'reply-language'
  | 'context'
  | 'webhook'
  | 'secret'
  | 'header'
  | 'timeout'
  | 'divisions'
  | 'custom-fields'
  | 'info-fields';

interface ActionInstanceCardProps {
  value: EditableAction;
  /** Types the org may create (GET /api/org-actions `allowedTypes`); the picker offers only these. */
  allowedTypes: string[];
  /** Types whose use is charged to the org (GET /api/org-actions `billedTypes`); a saved card marks them. */
  billedTypes: string[];
  divisions: GenesysDivision[] | null;
  genesysOrgId: string;
  genesysRegion: string;
  expanded: boolean;
  dirty: boolean;
  saving: boolean;
  /** Brief confirmation after a successful Save or Create (section clears it after ~2 s). */
  justSaved: boolean;
  /** True while another card's save or delete is in flight. */
  locked: boolean;
  error: string | null;
  /** Field to mark invalid and focus; a new nonce re-focuses after a repeated failure. */
  errorFocus: { field?: ActionField; nonce: number } | null;
  /** Dialog only: the input behind the footer's missing-requirement message, marked invalid (not focused). */
  blockedField?: ActionField | null;
  /** Success text; the section removes it after ~4 s, and `id` restarts the fade. */
  saveMessage: SaveMessage | null;
  saveError: string | null;
  /** Server-side CUSTOM_FIELDS_INVALID resolved to this action, if any. */
  customFieldsError: CustomFieldsServerError | null;
  /** Server-side INFO_FIELDS_INVALID resolved to this action, if any. */
  infoFieldsError?: InfoFieldsServerError | null;
  /**
   * Conversation info: the clash these form settings would have with another
   * enabled info action if this one were enabled; null when there is none.
   */
  infoConflict?: string | null;
  /** Conversation info, card only: the same for the saved settings, which keeps the switch off. */
  enableConflict?: string | null;
  /** Conversation info, card only: the saved action has at least one field. */
  infoFieldsSaved?: boolean;
  /** Conversation info: attribute names the other saved info actions use. */
  otherInfoKeys?: ReadonlySet<string>;
  /** The edits came back after Sign in again, but the secret typed before it did not. */
  secretNotRestored?: boolean;
  onToggleExpand: () => void;
  onChange: (patch: Partial<EditableAction>) => void;
  onSave: () => void;
  onDelete: () => void;
  /** Saved action only: discard unsaved edits and collapse (see ConversationActionsSection). */
  onCancelEdits: () => void;
  /** Header switch: persists at once for a saved action (see ConversationActionsSection). */
  onToggleEnabled: (enabled: boolean) => void;
  onCatalogImported: (summary: CatalogSummary) => void;
  /**
   * Card: the saved webhook settings have a stored successful test (`webhookTestedAt`).
   * Dialog: the settings typed there passed a test in the dialog.
   */
  webhookTested: boolean;
  /** Card only: the form's unsaved webhook settings passed a test; a Save of them keeps it. */
  unsavedWebhookTested?: boolean;
  /** Tests the webhook settings on the form, saved or not (see ConversationActionsSection); null when nothing was sent. */
  onTest?: () => Promise<TestWebhookResult | null>;
  /**
   * `dialog`: the settings form of a new action inside the Add action dialog,
   * which supplies the type picker, feedback and Create; no header or footer.
   */
  variant?: 'card' | 'dialog';
}

/** Send Files guide link under the saved type chip's description (the Add action dialog links the overview guide). */
function SendFilesGuideHint() {
  return (
    <p className="form-hint">
      <RichText
        k="settings-actions-files-help"
        values={{
          guide: (
            <a href={SEND_FILES_HELP_URL} target="_blank" rel="noopener noreferrer">
              {t('settings-actions-files-help-link')}
            </a>
          ),
        }}
      />
    </p>
  );
}

function catalogAgeText(updatedAt: string): string {
  const ts = Date.parse(updatedAt);
  if (!Number.isFinite(ts)) return '';
  const days = Math.floor((Date.now() - ts) / 86_400_000);
  if (days <= 0) return t('settings-actions-catalog-updated-today');
  if (days === 1) return t('settings-actions-catalog-updated-yesterday');
  return t('settings-actions-catalog-updated-days', { days: String(days) });
}

/**
 * One Conversation Actions instance: config form, webhook test, catalog
 * upload/download, and the sample-payload viewer. Edits go up through
 * onChange; Save on this card persists this instance (the PUT is still a
 * full list replace; other cards are sent as last-saved). Expand is
 * accordion-controlled by the section. Webhook test and catalog operations
 * run against the last SAVED state, so they are disabled until the instance
 * has a server-generated actionId.
 */
export function ActionInstanceCard({
  value,
  allowedTypes,
  billedTypes,
  divisions,
  genesysOrgId,
  genesysRegion,
  expanded,
  dirty,
  saving,
  justSaved,
  locked,
  error,
  errorFocus,
  blockedField = null,
  saveMessage,
  saveError,
  customFieldsError,
  infoFieldsError = null,
  infoConflict = null,
  enableConflict = null,
  infoFieldsSaved = false,
  otherInfoKeys,
  secretNotRestored = false,
  onToggleExpand,
  onChange,
  onSave,
  onDelete,
  onCancelEdits,
  onToggleEnabled,
  onCatalogImported,
  webhookTested,
  unsavedWebhookTested = false,
  onTest,
  variant = 'card',
}: ActionInstanceCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestWebhookResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [transfer, setTransfer] = useState<CatalogTransferProgress | null>(null);
  const [importSummary, setImportSummary] = useState<CatalogImportSummary | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [pendingReplaceFile, setPendingReplaceFile] = useState<File | null>(null);
  const [secretVisible, setSecretVisible] = useState(false);
  const [sampleCopied, setSampleCopied] = useState(false);
  const sampleCopiedTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (sampleCopiedTimer.current) window.clearTimeout(sampleCopiedTimer.current);
  }, []);

  const cardRef = useRef<HTMLDivElement>(null);
  const summaryButtonRef = useRef<HTMLButtonElement>(null);
  const wasExpandedRef = useRef(expanded);
  useEffect(() => {
    const wasExpanded = wasExpandedRef.current;
    wasExpandedRef.current = expanded;
    // Save closes the card while focus is still on the Save button, which unmounts with the form.
    if (wasExpanded && !expanded && justSaved) summaryButtonRef.current?.focus();
  }, [expanded, justSaved]);
  const fieldId = (field: ActionField) => `action-${field}-${value.key}`;
  const invalidField = errorFocus?.field;
  const saveFeedbackId = `action-save-feedback-${value.key}`;
  /** className suffix + ARIA for an input the latest save error (or the dialog's blocker) points at. */
  const invalidProps = (field: ActionField) => (invalidField === field || blockedField === field
    ? { invalid: ' form-input--invalid', aria: { 'aria-invalid': true, 'aria-describedby': saveFeedbackId } }
    : { invalid: '', aria: {} });

  useEffect(() => {
    if (!errorFocus || !expanded) return;
    // Deferred one task: the custom fields editor marks its rows in a follow-up render.
    const timer = window.setTimeout(() => {
      const root = cardRef.current;
      if (!root) return;
      const field = errorFocus.field;
      const byId = field && field !== 'custom-fields' && field !== 'info-fields' ? document.getElementById(fieldId(field)) : null;
      const target = byId ?? (field ? root.querySelector<HTMLElement>('[aria-invalid="true"]') : null);
      target?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
    // Re-run only for a new failed save, not for every edit while it is shown.
  }, [errorFocus?.nonce, expanded]);

  const saved = Boolean(value.actionId);
  const inDialog = variant === 'dialog';
  const typeChosen = Boolean(value.type);
  // A saved action keeps working through Save (the toolbar accepts existing instances of a
  // since-disallowed type); the runtime hides it from agents, so the card says so.
  const typeUnavailable = saved && !allowedTypes.includes(value.type);
  const billed = billedTypes.includes(value.type);

  // A saved card and the Add action dialog both test the settings on the form, saved or not.
  // The button stays disabled until those settings would let the test run.
  const testNeed = webhookTestNeed(value);
  const canTest = (saved || inDialog) && Boolean(onTest) && !testNeed;
  const testHintId = `action-test-hint-${value.key}`;
  const testResultRef = useRef<HTMLDivElement>(null);
  // Bumped when a test finishes; the result under the button is then scrolled into view.
  const [testDone, setTestDone] = useState(0);
  useEffect(() => {
    if (testDone) testResultRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [testDone]);
  const handleTest = async () => {
    if (!canTest || testing) return;
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    try {
      const result = onTest ? await onTest() : null;
      if (result) setTestResult(result);
    } catch (e) {
      setTestError(caughtErrorMessage(e, 'settings-actions-test-error'));
    } finally {
      setTesting(false);
      setTestDone((n) => n + 1);
    }
  };

  // A result describes the settings it tested; editing them clears it (the section's token no longer matches either).
  const typedWebhookSettings = JSON.stringify([value.webhookUrl, value.authType, value.headerName, value.timeoutSeconds, value.secret]);
  const lastTypedWebhookSettings = useRef(typedWebhookSettings);
  useEffect(() => {
    if (typedWebhookSettings === lastTypedWebhookSettings.current) return;
    lastTypedWebhookSettings.current = typedWebhookSettings;
    setTestResult(null);
    setTestError(null);
  }, [typedWebhookSettings]);

  const clearFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const runCatalogUpload = async (file: File) => {
    if (!value.actionId || uploading) return;
    setUploading(true);
    setImportSummary(null);
    setUploadError(null);
    try {
      const precheck = await precheckCatalogFile(file);
      if (!precheck.ok) {
        setUploadError(t(precheck.errorKey));
        return;
      }
      const result = await uploadActionCatalog(
        genesysOrgId,
        genesysRegion,
        value.actionId,
        file,
        setTransfer,
      );
      if (result.status && result.summary) {
        setImportSummary(result.summary);
        onCatalogImported({
          products: result.summary.products,
          imported: result.summary.imported,
          skipped: result.summary.skipped,
          duplicates: result.summary.duplicates,
          updatedAt: new Date().toISOString(),
        });
      } else if (result.code === CATALOG_TRANSFER_FAILED) {
        setUploadError(t('settings-actions-catalog-transfer-error'));
      } else {
        setUploadError(serverErrorMessage({
          code: result.code,
          cause: result.cause,
          knownKey: result.code ? undefined : 'settings-actions-catalog-upload-error',
          keepDetail: true,
        }));
      }
    } catch (e) {
      setUploadError(caughtErrorMessage(e, 'settings-actions-catalog-upload-error'));
    } finally {
      setUploading(false);
      setTransfer(null);
      clearFileInput();
    }
  };

  const handleFileSelected = (file: File) => {
    if (!value.actionId || uploading) return;
    // Replacing a live catalog is immediate and has no undo (agents match against it
    // as soon as the import lands), so confirm first. A first upload has nothing to
    // lose and stays frictionless.
    const currentCount = value.catalogSummary?.products ?? 0;
    if (currentCount > 0) {
      setPendingReplaceFile(file);
      setUploadError(null);
      return;
    }
    void runCatalogUpload(file);
  };

  const cancelReplace = () => {
    setPendingReplaceFile(null);
    clearFileInput();
  };

  const handleDownloadCatalog = async () => {
    if (!value.actionId || downloading || uploading) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      const catalog = await downloadActionCatalog(genesysOrgId, genesysRegion, value.actionId, setTransfer);
      downloadTextFile(
        `mediabriz-catalog-${value.actionId}.csv`,
        catalogProductsToCsv(catalog.products),
        'text/csv;charset=utf-8',
      );
    } catch (e) {
      if (e instanceof CatalogTransferError) {
        setDownloadError(t('settings-actions-catalog-download-transfer-error'));
      } else {
        setDownloadError(caughtErrorMessage(e, 'settings-actions-catalog-download-error'));
      }
    } finally {
      setDownloading(false);
      setTransfer(null);
    }
  };

  /** Button text for the transfer in flight (upload, server import, or download). */
  const transferText = (progress: CatalogTransferProgress): string => {
    if (progress.phase === 'importing') return t('settings-actions-catalog-importing');
    const busyKey = `settings-actions-catalog-${progress.phase}`;
    return progress.percent === null
      ? t(busyKey)
      : t(`${busyKey}-percent`, { percent: String(progress.percent) });
  };

  const isReturn = value.type === 'CREATE_RETURN';
  // Send-files runs no analysis and has no catalog (the toolbar refuses an import).
  const sendsFiles = isSendFiles(value.type);
  // AI Reply has no catalog, webhook or custom fields: the agent sends the reply from the reply box.
  const aiReply = isAiReply(value.type);
  const hasCatalog = usesCatalog(value.type);
  // Conversation info is display only: no webhook, business context, custom fields, catalog or sample.
  const info = isConversationInfo(value.type);
  // A switched-off action stays off until these are done; switching off is always allowed.
  const requirements = saved
    ? enableRequirements(value.type, {
      webhookTested,
      catalogLoaded: catalogLoaded(value.catalogSummary),
      hasInfoFields: infoFieldsSaved,
    })
    : [];
  const requirementsDone = requirements.every((r) => r.done);
  const enableBlocked = !value.enabled && (!requirementsDone || Boolean(enableConflict));
  const enableConflictText = enableConflict ? `${enableConflict} ${t('settings-actions-info-conflict-fix')}` : null;
  // Saved and on: these settings would clash, so Save stays off until the divisions change.
  const saveConflict = saved && value.enabled && Boolean(infoConflict);
  // The readiness note of a switched-off card already says it when the form still has the saved divisions.
  const divisionConflict = saved && !value.enabled && infoConflict === enableConflict ? null : infoConflict;
  const switchTitle = !enableBlocked
    ? t(value.enabled ? 'settings-actions-toggle-on-help' : 'settings-actions-toggle-off-help')
    : info
      ? (requirementsDone && enableConflictText) || t('settings-actions-ready-need-fields')
      : t(hasCatalog ? 'settings-actions-toggle-blocked-catalog' : 'settings-actions-toggle-blocked');
  const readinessId = `action-readiness-${value.key}`;
  const samplePayload = buildSampleWebhookPayload({
    type: value.type,
    organizationId: genesysOrgId,
    actionId: value.actionId || '',
    label: value.label,
    customFields: sampleCustomFieldValues(value.customFields),
  });
  const sampleJson = JSON.stringify(samplePayload, null, 2);
  const replyLanguageOptions = replyLanguageOptionGroups();

  const copySampleJson = async () => {
    await copyText(sampleJson);
    setSampleCopied(true);
    if (sampleCopiedTimer.current) window.clearTimeout(sampleCopiedTimer.current);
    sampleCopiedTimer.current = window.setTimeout(() => setSampleCopied(false), 2000);
  };

  const toggleDivision = (id: string, checked: boolean) => {
    const next = checked
      ? [...value.divisionIds, id]
      : value.divisionIds.filter((d) => d !== id);
    onChange({ divisionIds: next });
  };

  // Saved IDs missing from the loaded divisions list are still sent on save, so they get rows
  // too. A row stays once shown, so unticking it can be undone.
  const [unlistedDivisionIds, setUnlistedDivisionIds] = useState<string[]>([]);
  const listedDivisionIds = new Set((divisions ?? []).map((d) => d.id));
  const newlyUnlisted = divisions
    ? value.divisionIds.filter((id) => !listedDivisionIds.has(id) && !unlistedDivisionIds.includes(id))
    : [];
  if (newlyUnlisted.length > 0) setUnlistedDivisionIds([...unlistedDivisionIds, ...newlyUnlisted]);
  const divisionCount = effectiveDivisionIds(value, divisions !== null).length;

  const bodyId = `action-body-${value.key}`;

  const labelField = (
    <div className="form-group">
      <label className="form-label" htmlFor={fieldId('label')}>
        {t('settings-actions-label-label')}
      </label>
      <input
        id={fieldId('label')}
        dir={fieldDir(value.label, t(typedKey('settings-actions-label-placeholder', value.type)))}
        className={`form-input${invalidProps('label').invalid}`}
        {...invalidProps('label').aria}
        type="text"
        maxLength={100}
        value={value.label}
        placeholder={t(typedKey('settings-actions-label-placeholder', value.type))}
        onChange={(e) => onChange({ label: e.target.value })}
      />
      <p className="form-hint">
        {t(aiReply ? 'settings-actions-label-help-reply' : info ? 'settings-actions-label-help-info' : 'settings-actions-label-help')}
      </p>
    </div>
  );

  return (
    <div
      ref={cardRef}
      className={inDialog
        ? 'action-card-form'
        : `action-card${expanded ? ' action-card--expanded' : ' action-card--collapsed'}${!saved || value.enabled ? '' : ' action-card--disabled'}`}
    >
      {!inDialog && (
      <div className="action-card__header">
        <button
          type="button"
          ref={summaryButtonRef}
          className="action-card__summary"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={onToggleExpand}
        >
          <svg
            className={`action-card__chevron${expanded ? ' action-card__chevron--open' : ''}`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span className="action-card__summary-text">
            <span className="action-card__title" title={value.label.trim() || undefined}>
              <bdi>{value.label.trim() || t('settings-actions-new-label')}</bdi>
            </span>
            {!expanded && (
              <span className="action-card__summary-meta">
                {actionTypeLabel(value.type)}
                {/* Inside the expand button: no tooltip, the hidden text joins the button's name. */}
                {billed && (
                  <span className="action-card__summary-billed" title={t('settings-actions-billed-tooltip')}>
                    <BilledIcon />
                    <span className="sr-only">{t('settings-actions-billed-short')}</span>
                  </span>
                )}
                {saved && hasCatalog && (
                  <>
                    {' · '}
                    {value.catalogSummary ? (
                      t('settings-actions-catalog-count', {
                        count: formatNumber(value.catalogSummary.products, getCurrentLocale()),
                      })
                    ) : (
                      <span className="action-card__summary-catalog--missing">
                        {t('settings-actions-summary-no-catalog')}
                      </span>
                    )}
                  </>
                )}
                {' · '}
                {divisionCount === 0
                  ? t('settings-actions-summary-divisions-all')
                  : divisionCount === 1
                    ? t('settings-actions-summary-divisions-one')
                    : t('settings-actions-summary-divisions-many', { count: String(divisionCount) })}
              </span>
            )}
          </span>
        </button>
        <div className="action-card__header-controls">
          {saved && justSaved && (
            <span className="action-card__badge action-card__badge--saved">
              {t('settings-actions-badge-saved')}
            </span>
          )}
          {saved && !justSaved && dirty && (
            <span className="action-card__badge action-card__badge--unsaved">
              {t('settings-actions-badge-unsaved')}
            </span>
          )}
          {saved && (
            <>
              <span
                className={`action-card__badge ${value.enabled ? 'action-card__badge--enabled' : 'action-card__badge--disabled'}`}
              >
                {value.enabled ? t('settings-actions-badge-enabled') : t('settings-actions-badge-disabled')}
              </span>
              <button
                type="button"
                role="switch"
                className="action-switch"
                aria-checked={value.enabled}
                aria-label={t('settings-actions-toggle-label')}
                title={switchTitle}
                aria-describedby={enableBlocked && expanded ? readinessId : undefined}
                onClick={() => onToggleEnabled(!value.enabled)}
                disabled={saving || locked || enableBlocked}
              >
                <span className="action-switch__thumb" aria-hidden="true" />
              </button>
            </>
          )}
          <button
            type="button"
            className="btn btn--secondary btn--small"
            onClick={onDelete}
            disabled={saving || locked}
          >
            {t('settings-actions-delete')}
          </button>
        </div>
      </div>
      )}

      {/* The form closes after a successful Save. The Saved button and the short alert stay. */}
      {!inDialog && !expanded && (justSaved || (saveMessage && !error && !saveError)) && (
        <SaveRow
          className="action-card__save action-card__save--collapsed"
          message={saveMessage && !error && !saveError ? saveMessage : null}
        >
          {justSaved && <SaveButton saving={false} justSaved label={t('settings-actions-save')} />}
        </SaveRow>
      )}

      {(expanded || inDialog) && (
      <div id={bodyId} className="action-card__body">
      {inDialog ? labelField : (
      /* The type is chosen once, in the Add action dialog: an action keeps its analysis and payload shape. */
      <div className="form-row">
        <div className="form-group">
          <span className="form-label">{t('settings-actions-type-label')}</span>
          <span className="action-type-chip">
            {actionTypeLabel(value.type)}
            {billed && <BilledMark tooltipId={`${bodyId}-billed`} focusable />}
          </span>
          <p className="form-hint">{actionTypeDescription(value.type) || t('settings-actions-type-fixed-help')}</p>
          {sendsFiles && <SendFilesGuideHint />}
          {typeUnavailable && (
            <p className="form-hint action-type-unavailable" role="status">
              {t('settings-actions-type-unavailable')}
            </p>
          )}
        </div>
        {labelField}
      </div>
      )}

      {saved && !value.enabled && requirements.length > 0 && (
        <div className={`action-readiness${enableBlocked ? '' : ' action-readiness--done'}`} id={readinessId}>
          <p className="action-readiness__message">
            {readinessInstruction(requirements, unsavedWebhookTested) ?? enableConflictText ?? t('settings-actions-ready-title-done')}
          </p>
        </div>
      )}

      {/* Type-specific settings; a card always has a type (guard for a malformed stored row). */}
      {typeChosen && (
      <>
      {!sendsFiles && !info && (
      <div className="form-group">
        <label className="form-label" htmlFor={fieldId('context')}>
          {t(aiReply ? 'settings-actions-context-label-reply' : 'settings-actions-context-label')}
        </label>
        <textarea
          id={fieldId('context')}
          dir={fieldDir(value.businessContext, t(typedKey('settings-actions-context-placeholder', value.type)))}
          className={`form-input form-textarea${invalidProps('context').invalid}`}
          {...invalidProps('context').aria}
          rows={3}
          maxLength={BUSINESS_CONTEXT_MAX_LENGTH}
          value={value.businessContext}
          placeholder={t(typedKey('settings-actions-context-placeholder', value.type))}
          onChange={(e) => onChange({ businessContext: e.target.value })}
        />
        <div className="form-hint-row">
          <p className="form-hint">{t(typedKey('settings-actions-context-help', value.type))}</p>
          <span className="form-counter" aria-live="polite">
            {value.businessContext.length}/{BUSINESS_CONTEXT_MAX_LENGTH}
          </span>
        </div>
      </div>
      )}

      {aiReply && (
      <div className="form-group">
        <label className="form-label" htmlFor={fieldId('reply-language')}>
          {t('settings-actions-reply-language-label')}
        </label>
        <select
          id={fieldId('reply-language')}
          className={`form-select${invalidProps('reply-language').invalid}`}
          {...invalidProps('reply-language').aria}
          value={value.replyLanguage}
          onChange={(e) => onChange({ replyLanguage: e.currentTarget.value })}
        >
          <optgroup label={t('settings-actions-reply-language-common')}>
            {value.replyLanguage && !isReplyLanguage(value.replyLanguage) && (
              <option value={value.replyLanguage}>{replyLanguageLabel(value.replyLanguage)}</option>
            )}
            {replyLanguageOptions.common.map((option) => (
              <option key={option.code} value={option.code}>{option.label}</option>
            ))}
          </optgroup>
          <optgroup label={t('settings-actions-reply-language-more')}>
            {replyLanguageOptions.more.map((option) => (
              <option key={option.code} value={option.code}>{option.label}</option>
            ))}
          </optgroup>
        </select>
        <p className="form-hint">{t('settings-actions-reply-language-help')}</p>
      </div>
      )}

      {info && (
      <InfoFieldsEditor
        actionKey={value.key}
        fields={value.infoFields}
        onChange={(infoFields) => onChange({ infoFields })}
        serverError={infoFieldsError}
        otherActionKeys={otherInfoKeys}
        showAllErrors={Boolean(error)
          || (blockedField === 'info-fields' && !value.infoFields.some((f) => f.unsaved))}
      />
      )}

      {!aiReply && !info && (
      <CustomFieldsEditor
        actionKey={value.key}
        actionType={value.type}
        fields={value.customFields}
        onChange={(customFields) => onChange({ customFields })}
        serverError={customFieldsError}
        showAllErrors={Boolean(error)
          || (blockedField === 'custom-fields' && !value.customFields.some((f) => f.unsaved))}
      />
      )}

      {/* A new action chooses Enabled or Disabled in the Add action dialog's Create step instead. */}
      {saved && (
      <p className="form-hint action-card__enabled-hint">
        {t(aiReply ? 'settings-actions-enabled-help-reply' : info ? 'settings-actions-enabled-help-info' : 'settings-actions-enabled-help')}
      </p>
      )}

      {!aiReply && !info && (
      <>
      <div className="form-group">
        <label className="form-label" htmlFor={fieldId('webhook')}>
          {t('settings-actions-webhook-label')}
        </label>
        <input
          id={fieldId('webhook')}
          className={`form-input${invalidProps('webhook').invalid}`}
          {...invalidProps('webhook').aria}
          type="url"
          dir="ltr"
          value={value.webhookUrl}
          placeholder={t(typedKey('settings-actions-webhook-placeholder', value.type))}
          onChange={(e) => onChange({ webhookUrl: e.target.value })}
        />
        <p className="form-hint">{t(typedKey('settings-actions-webhook-help', value.type))}</p>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor={`action-auth-${value.key}`}>
            {t('settings-actions-auth-label')}
          </label>
          <select
            id={`action-auth-${value.key}`}
            className="form-select"
            value={value.authType}
            onChange={(e) => onChange({ authType: e.target.value as EditableAction['authType'] })}
          >
            <option value="none">{t('settings-actions-auth-none')}</option>
            <option value="bearer">{t('settings-actions-auth-bearer')}</option>
            <option value="header">{t('settings-actions-auth-header')}</option>
          </select>
          <p className="form-hint">{t('settings-actions-auth-help')}</p>
        </div>
        {value.authType === 'header' && (
          <div className="form-group">
            <label className="form-label" htmlFor={fieldId('header')}>
              {t('settings-actions-header-name-label')}
            </label>
            <input
              id={fieldId('header')}
              className={`form-input${invalidProps('header').invalid}`}
              {...invalidProps('header').aria}
              type="text"
              dir="ltr"
              maxLength={100}
              value={value.headerName}
              placeholder={t('settings-actions-header-name-placeholder')}
              onChange={(e) => onChange({ headerName: e.target.value })}
            />
          </div>
        )}
        <div className="form-group">
          <label className="form-label" htmlFor={fieldId('timeout')}>
            {t('settings-actions-timeout-label')}
          </label>
          <input
            id={fieldId('timeout')}
            className={`form-input${invalidProps('timeout').invalid}`}
            {...invalidProps('timeout').aria}
            type="number"
            min={3}
            max={20}
            step={1}
            value={value.timeoutSeconds}
            onChange={(e) => onChange({ timeoutSeconds: Number(e.target.value) })}
          />
          <p className="form-hint">{t('settings-actions-timeout-help')}</p>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor={fieldId('secret')}>
          {t('settings-actions-secret-label')}
        </label>
        <div className="form-input-reveal" dir={fieldDir(value.secret, t('settings-actions-secret-placeholder'), true)}>
          <input
            id={fieldId('secret')}
            className={`form-input${invalidProps('secret').invalid}`}
            {...invalidProps('secret').aria}
            type={secretVisible ? 'text' : 'password'}
            dir={fieldDir(value.secret, t('settings-actions-secret-placeholder'), true)}
            autoComplete="new-password"
            value={value.secret}
            placeholder={t('settings-actions-secret-placeholder')}
            onChange={(e) => onChange({ secret: e.target.value })}
          />
          <button
            type="button"
            className="form-input-reveal__toggle"
            aria-label={secretVisible ? t('settings-actions-secret-hide') : t('settings-actions-secret-show')}
            onClick={() => setSecretVisible((v) => !v)}
          >
            {secretVisible ? (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
        {secretNotRestored && !value.secret ? (
          <p className="form-hint form-hint--warning" role="status">{t('settings-actions-secret-not-kept')}</p>
        ) : (
          <p className="form-hint">
            {value.hasSecret && !value.secret
              ? t('settings-actions-secret-set')
              : t('settings-actions-secret-help')}
          </p>
        )}
      </div>
      </>
      )}

      <div className="form-group">
        <label className="form-label" htmlFor={divisions ? undefined : fieldId('divisions')}>
          {t('settings-actions-divisions-label')}
        </label>
        {divisions ? (
          <>
            <div className="action-divisions">
              {unlistedDivisionIds.map((id) => (
                <label key={id} className="action-divisions__item action-divisions__item--unlisted">
                  <input
                    type="checkbox"
                    {...(DIVISION_ID_RE.test(id) || !value.divisionIds.includes(id) ? {} : invalidProps('divisions').aria)}
                    checked={value.divisionIds.includes(id)}
                    onChange={(e) => toggleDivision(id, e.target.checked)}
                  />
                  <span>{t('settings-actions-divisions-unlisted-row', {
                    label: t('settings-actions-divisions-unlisted'),
                    id,
                  })}</span>
                </label>
              ))}
              {divisions.map((d) => (
                <label key={d.id} className="action-divisions__item">
                  <input
                    type="checkbox"
                    checked={value.divisionIds.includes(d.id)}
                    onChange={(e) => toggleDivision(d.id, e.target.checked)}
                  />
                  <span><bdi>{d.name}</bdi></span>
                </label>
              ))}
            </div>
            <p className="form-hint">
              {t('settings-actions-divisions-scope')}{' '}
              {t(unlistedDivisionIds.length > 0 ? 'settings-actions-divisions-help-unlisted' : 'settings-actions-divisions-help')}
            </p>
          </>
        ) : (
          <>
            <input
              id={fieldId('divisions')}
              dir={fieldDir(value.divisionsText, t('settings-actions-divisions-manual-placeholder'), true)}
              className={`form-input${invalidProps('divisions').invalid}`}
              {...invalidProps('divisions').aria}
              type="text"
              value={value.divisionsText}
              placeholder={t('settings-actions-divisions-manual-placeholder')}
              onChange={(e) => onChange({ divisionsText: e.target.value })}
            />
            <p className="form-hint">
              {t('settings-actions-divisions-scope')}{' '}
              {t('settings-actions-divisions-manual-help')}
            </p>
          </>
        )}
        {info && <p className="form-hint">{t('settings-actions-info-divisions-help')}</p>}
        {divisionConflict && (saveConflict ? (
          <p className="form-error" role="alert">
            {divisionConflict} {t('settings-actions-info-conflict-save-fix')}
          </p>
        ) : (
          <p className="form-hint action-divisions__conflict" role="status">
            {divisionConflict} {t('settings-actions-info-conflict-fix')}
          </p>
        ))}
      </div>

      {!aiReply && !info && (
      <div className="action-card__actions">
        <button
          type="button"
          className="btn btn--primary action-strong-btn"
          onClick={handleTest}
          disabled={!canTest || testing}
          aria-describedby={testHintId}
        >
          {testing ? t('settings-actions-testing') : t('settings-actions-test')}
        </button>
        <span className="form-hint action-card__actions-hint" id={testHintId}>
          {testNeed
            ? t('settings-actions-test-needs-line', { requirement: t(testNeed) })
            : saved
              ? `${t(typedKey('settings-actions-test-hint', value.type))} ${t('settings-actions-test-form-hint')}`
              : canTest
                ? `${t(typedKey('settings-actions-test-hint', value.type))} ${t('settings-actions-test-draft-hint')}`
                : t('settings-actions-test-save-first')}
        </span>
      </div>
      )}

      <div ref={testResultRef} className="action-test-results">
      {testResult && (
        <p
          className={`action-test-result ${testResult.success ? 'action-test-result--success' : 'action-test-result--failure'}`}
          role="status"
        >
          {[
            testResult.success ? t('settings-actions-test-success') : t('settings-actions-test-failure'),
            testResult.statusCode !== null
              ? t('settings-actions-test-status-line', { status: testResult.statusCode })
              : '',
            testResult.referenceId
              ? t('settings-actions-test-reference-line', { reference: testResult.referenceId })
              : '',
            testResult.message ?? '',
          ].filter(Boolean).join(' ')}
        </p>
      )}
      {testError && (
        <p className="action-test-result action-test-result--failure" role="alert">
          {testError}
        </p>
      )}
      </div>

      {saved && hasCatalog && (
        <div className="action-catalog">
          <p className="action-catalog__title">{t('settings-actions-catalog-title')}</p>
          {value.catalogSummary ? (
            <p className="action-catalog__meta">
              {t('settings-actions-catalog-count', {
                count: formatNumber(value.catalogSummary.products, getCurrentLocale()),
              })}
              {' · '}
              {catalogAgeText(value.catalogSummary.updatedAt)}
            </p>
          ) : (
            <p className="settings-page__notice settings-page__notice--warning action-catalog__notice" role="status">
              {t('settings-actions-catalog-missing')}
            </p>
          )}
          <div className="action-card__actions">
            <input
              ref={fileInputRef}
              className="hidden-input"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFileSelected(file);
              }}
            />
            <button
              type="button"
              className="btn btn--primary btn--small"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || downloading || Boolean(pendingReplaceFile)}
            >
              {uploading
                ? transferText(transfer ?? { phase: 'uploading', percent: null })
                : t('settings-actions-catalog-upload')}
            </button>
            {value.catalogSummary && (
              <button
                type="button"
                className="btn btn--secondary btn--small"
                onClick={handleDownloadCatalog}
                disabled={downloading || uploading}
              >
                {downloading
                  ? transferText(transfer ?? { phase: 'downloading', percent: null })
                  : t('settings-actions-catalog-download')}
              </button>
            )}
            <button
              type="button"
              className="btn btn--secondary btn--small"
              onClick={downloadTemplateCsv}
            >
              {t('settings-actions-catalog-template')}
            </button>
          </div>
          {transfer && (
            <progress
              className="action-catalog__progress"
              max={100}
              value={transfer.percent ?? undefined}
              aria-label={transferText(transfer)}
            />
          )}
          {pendingReplaceFile && (
            <div className="settings-page__notice settings-page__notice--warning action-catalog__confirm" role="status">
              <p>
                {t('settings-actions-catalog-replace-confirm', {
                  count: formatNumber(value.catalogSummary?.products ?? 0, getCurrentLocale()),
                })}
              </p>
              <p>{t('settings-actions-catalog-in-use')}</p>
              <div className="action-catalog__confirm-actions">
                <button
                  type="button"
                  className="btn btn--primary btn--small"
                  onClick={() => {
                    const file = pendingReplaceFile;
                    setPendingReplaceFile(null);
                    void runCatalogUpload(file);
                  }}
                >
                  {t('settings-actions-catalog-replace-continue')}
                </button>
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={cancelReplace}
                >
                  {t('settings-actions-catalog-replace-cancel')}
                </button>
              </div>
            </div>
          )}
          <p className="form-hint">{t('settings-actions-catalog-template-hint')}</p>
          <p className="form-hint">
            <RichText
              k="settings-actions-catalog-help"
              values={{
                guide: (
                  <a href={ACTIONS_CATALOG_HELP_URL} target="_blank" rel="noopener noreferrer">
                    {t('settings-actions-catalog-help-link')}
                  </a>
                ),
              }}
            />
          </p>
          <CatalogRowManager
            genesysOrgId={genesysOrgId}
            genesysRegion={genesysRegion}
            actionId={value.actionId || ''}
            catalogSummary={value.catalogSummary}
            onCatalogChanged={onCatalogImported}
          />
          <details className="action-columns">
            <summary>{t('settings-actions-columns-title')}</summary>
            <p className="action-columns__intro">{t('settings-actions-columns-intro')}</p>
            <ul className="action-columns__list">
              {CATALOG_COLUMNS.map((col) => (
                <li key={col.name} className="action-columns__item">
                  <code className="action-columns__name">{col.name}</code>
                  <span
                    className={`action-columns__tag${col.required ? ' action-columns__tag--required' : ''}`}
                  >
                    {col.required
                      ? t('settings-actions-columns-required')
                      : t('settings-actions-columns-optional')}
                  </span>
                  <span className="action-columns__desc">{t(col.descKey)}</span>
                </li>
              ))}
            </ul>
            <p className="action-columns__note">{t('settings-actions-columns-replace')}</p>
          </details>
          {uploadError && (
            <p className="form-error" role="alert">
              {uploadError}
            </p>
          )}
          {downloadError && (
            <p className="form-error" role="alert">
              {downloadError}
            </p>
          )}
          {importSummary && (
            <div className="action-import-summary" role="status">
              <p className="action-import-summary__title">{t('settings-actions-import-title')}</p>
              <p>
                {t('settings-actions-import-products', { count: formatNumber(importSummary.products, getCurrentLocale()) })}
                {' · '}
                {t('settings-actions-import-imported', { count: formatNumber(importSummary.imported, getCurrentLocale()) })}
                {' · '}
                {t('settings-actions-import-skipped', { count: formatNumber(importSummary.skipped, getCurrentLocale()) })}
                {' · '}
                {t('settings-actions-import-duplicates', { count: formatNumber(importSummary.duplicates, getCurrentLocale()) })}
              </p>
              <p className="action-import-summary__note">{t('settings-actions-catalog-in-use')}</p>
              {importSummary.errors.length > 0 && (
                <ul className="action-import-summary__errors" dir="ltr">
                  {importSummary.errors.slice(0, 10).map((err, i) => (
                    <li key={i}>
                      {t('settings-actions-import-line', { line: err.line, reason: err.reason })}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {!aiReply && !info && (
      <details className="action-sample">
        <summary>{t('settings-actions-sample-title')}</summary>
        <p className="action-sample__intro">{t(typedKey('settings-actions-sample-intro', value.type))}</p>
        <p className="form-hint">{t('settings-actions-sample-agent-email')}</p>
        <p className="form-hint">{t('settings-actions-sample-external-tag')}</p>
        {isReturn && (
          <>
            <p className="form-hint">{t('settings-actions-sample-return-items')}</p>
            <p className="form-hint">{t('settings-actions-sample-return-fields')}</p>
          </>
        )}
        {sendsFiles ? (
          <>
            <p className="form-hint">{t('settings-actions-sample-files-list')}</p>
            <p className="form-hint">{t('settings-actions-sample-files-download')}</p>
          </>
        ) : (
          <p className="form-hint">{t('settings-actions-sample-po')}</p>
        )}
        <p className="form-hint">{t('settings-actions-sample-custom-fields')}</p>
        <div className="action-sample__pre-wrap">
          <pre className="action-sample__pre" dir="ltr">{sampleJson}</pre>
          <button
            type="button"
            className="action-sample__copy"
            onClick={() => void copySampleJson()}
            aria-label={sampleCopied ? t('settings-actions-sample-copied') : t('settings-actions-sample-copy')}
            title={sampleCopied ? t('settings-actions-sample-copied') : t('settings-actions-sample-copy')}
          >
            {sampleCopied ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
          </button>
        </div>
        <div className="action-card__actions">
          <button
            type="button"
            className="btn btn--secondary btn--small"
            onClick={() => downloadTextFile(
              sendsFiles
                ? 'mediabriz-sample-files-payload.json'
                : isReturn ? 'mediabriz-sample-return-payload.json' : 'mediabriz-sample-order-payload.json',
              sampleJson,
              'application/json',
            )}
          >
            {t('settings-actions-sample-download')}
          </button>
        </div>
        <p className="action-sample__note">
          <RichText
            k={sendsFiles ? 'settings-actions-sample-spec-files' : 'settings-actions-sample-spec'}
            values={{
              guide: (
                <a href={ACTIONS_WEBHOOK_HELP_URL} target="_blank" rel="noopener noreferrer">
                  {t('settings-actions-sample-spec-link')}
                </a>
              ),
            }}
          />
        </p>
      </details>
      )}
      </>
      )}

      {/* Feedback sits beside the buttons so the result of a click is where the click was. */}
      {!inDialog && (
      <SaveRow className="action-card__save" feedbackId={saveFeedbackId} error={error || saveError} message={saveMessage}>
        <button
          type="button"
          className="btn btn--secondary"
          {...discardButtonProps}
          onClick={onCancelEdits}
          disabled={saving || locked}
          title={t(dirty ? 'settings-actions-cancel-edits-help' : 'settings-actions-cancel-edits-clean-help')}
        >
          {t('settings-actions-cancel-edits')}
        </button>
        <SaveButton
          saving={saving}
          justSaved={justSaved}
          label={t('settings-actions-save')}
          onClick={() => onSave()}
          disabled={locked || !dirty || saveConflict}
          title={saveConflict ? t('settings-actions-info-conflict-save-fix') : undefined}
        />
      </SaveRow>
      )}
      </div>
      )}
    </div>
  );
}
